// TableScope: the DagScope that projects one member table out of its group's
// physical DAG (cf. RSet's NestedElementScope).
//
// Payloads: a row op appended through a member RTable is wrapped into a
// `{action: 'row', table, op}` envelope. Reading back, an envelope unwraps to
// its inner op; a group `create` (initial rows) or `bundle` entry unwraps to a
// `{action: 'rows', ops}` slice carrying this table's ops only.
//
// Meta scheme (group DAG entries; all derived from the payload + the
// effective schema at the entry position, and re-checked at validation since
// meta is unhashed):
//
//   tables: [touched table names]            - every row-carrying entry
//   t-<table>-rows: [rowId]                  - row cover queries (liveness)
//   t-<table>-cols: ['<rowId>:<incarnationId>:<column>'] - column writes
//                                              updates): the per-column LWW
//                                              cover queries, keyed by the
//                                              schema birth write active at
//                                              write time (see resolve.ts).
//   t-<table>-pub-<column>: [normalized]     - pub export (inserts and updates;
//                                              stale values in old entries are
//                                              candidate noise, filtered by the
//                                              resolved-value re-check in
//                                              findRowIds)
//   t-<table>-author: [keyId]                - insert author system field
//   barrier: ['t']                           - deletes in concurrentDeletes
//                                              tables (and schema deploys;
//                                              tagged in group.ts)
//
// `barrier` stays an OUTER (unprefixed) key: it is the global revision tag.
// wrapMeta hoists an inner barrier to the outer key, and wrapFilter passes it
// through unprefixed, so table-scoped concurrent-barrier queries see it.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { json } from "@hyper-hyper-space/hhs3_json";
import { position } from "@hyper-hyper-space/hhs3_dag";
import { validationFailure, validationOk, wrapValidationFailure } from "@hyper-hyper-space/hhs3_mvt";
// The combined (rowId, column incarnation, column name) meta value for
// column-write cover queries. Column name is included because base columns
// from one add-table rule share the same birth (entryHash, ruleIndex).
export function colTag(rowId, incarnationId, column) {
    return rowId + ':' + incarnationId + ':' + column;
}
// Extract one table's row ops from a GROUP-level entry payload (row envelope,
// group create, or bundle). Mirrors TableScope.unwrapPayload's op selection;
// used by delta accumulation, which walks the raw group DAG (group-level
// payloads) rather than a table scope. A ref-advance / unknown action yields no
// ops.
export function tableOpsFromGroupPayload(payload, table) {
    var _a, _b;
    const p = payload;
    switch (p['action']) {
        case 'row': {
            const envelope = p;
            return envelope.table === table ? [envelope.op] : [];
        }
        case 'create': {
            const create = p;
            return ((_b = (_a = create.initialRows) === null || _a === void 0 ? void 0 : _a[table]) !== null && _b !== void 0 ? _b : []);
        }
        case 'bundle': {
            const bundle = p;
            return bundle.writes.filter((w) => w.table === table).map((w) => w.op);
        }
        default:
            return [];
    }
}
// Inner (table-scope) meta for one row op. `rows` indexes the touched row;
// inserts and updates tag every carried column (`cols`) and export their
// carried pub column values; deletes are ALWAYS barrier-tagged. Whether that
// barrier is HONORED (reaches concurrent branches) is decided at view time by
// the (at, from)-resolved concurrentDeletes flag, NOT here: the tag is baked
// permanently at write, but the flag is mutable + resolved at the horizon, so
// gating honoring on the tag's write-time value would make enabling the flag
// unable to honor a delete authored while it was off. Tagging unconditionally
// makes concurrentDeletes resolve fully at the view horizon (like restrictions
// / FKs); see liveInsert / baseLiveInsert in ../rtable/view.ts.
export function deriveRowOpInnerMeta(op, table, schemaView) {
    const meta = { rows: json.toSet([op.rowId]) };
    if (op.action === 'insert' || op.action === 'update') {
        const columns = Object.keys(op.values);
        if (columns.length > 0) {
            const tags = [];
            for (const column of columns) {
                const incarnation = schemaView.getColumnIncarnation(table, column);
                if (incarnation === undefined) {
                    throw new Error(`deriveRowOpInnerMeta: column '${column}' is not live on table '${table}'`);
                }
                tags.push(colTag(op.rowId, incarnation, column));
            }
            meta['cols'] = json.toSet(tags);
        }
        const pubColumns = schemaView.getPubColumns(table);
        for (const column of pubColumns) {
            const value = op.values[column];
            if (value !== undefined) {
                meta['pub-' + column] = json.toSet([json.toStringNormalized(value)]);
            }
        }
    }
    if (op.action === 'insert' && op.author !== undefined) {
        meta['author'] = json.toSet([op.author]);
    }
    if (op.action === 'delete') {
        meta['barrier'] = json.toSet(['t']);
    }
    return meta;
}
// Outer meta for one table's inner meta: `t-<table>-` prefix, except for the
// global `barrier` key, which is hoisted (merged) to the outer key.
function wrapInnerMeta(outer, table, innerMeta) {
    for (const key of Object.keys(innerMeta)) {
        if (key === 'barrier') {
            outer['barrier'] = innerMeta[key];
        }
        else {
            outer['t-' + table + '-' + key] = innerMeta[key];
        }
    }
}
// Outer meta for a row envelope. Used by the group's applyPayload (sync
// ingestion) and by validation re-derivation; the local append path produces
// the identical result through TableScope.wrapMeta.
export function deriveEnvelopeMeta(envelope, schemaView) {
    const meta = { tables: json.toSet([envelope.table]) };
    const op = envelope.op;
    wrapInnerMeta(meta, envelope.table, deriveRowOpInnerMeta(op, envelope.table, schemaView));
    return meta;
}
// Outer meta for a multi-row entry (group create / bundle): every touched
// table is tagged, and each op contributes its rows / cols / pub / barrier
// meta (per-table merged) so table scopes surface their slices. `tablesOps`
// maps each touched table to its ops IN ORDER.
function deriveMultiRowMeta(tablesOps, schemaView) {
    const meta = {};
    const tables = Object.keys(tablesOps);
    if (tables.length === 0)
        return meta;
    meta['tables'] = json.toSet(tables);
    for (const table of tables) {
        const inner = {};
        for (const op of tablesOps[table]) {
            const opMeta = deriveRowOpInnerMeta(op, table, schemaView);
            for (const key of Object.keys(opMeta)) {
                const existing = inner[key];
                inner[key] = existing === undefined
                    ? opMeta[key]
                    : json.toSet([...json.fromSet(existing), ...json.fromSet(opMeta[key])]);
            }
        }
        wrapInnerMeta(meta, table, inner);
    }
    return meta;
}
// Outer meta for a group create entry: every initial table is tagged, and each
// initial row contributes its rows / cols / pub meta so table scopes surface
// their genesis rows.
export function deriveCreateMeta(create, schemaView) {
    var _a;
    const initial = (_a = create.initialRows) !== null && _a !== void 0 ? _a : {};
    const tablesOps = {};
    for (const table of Object.keys(initial)) {
        tablesOps[table] = initial[table];
    }
    return deriveMultiRowMeta(tablesOps, schemaView);
}
// Outer meta for a bundle entry: the ordered writes grouped by table (order
// within a table preserved), each table's ops merged like a create.
export function deriveBundleMeta(bundle, schemaView) {
    var _a;
    var _b;
    const tablesOps = {};
    for (const write of bundle.writes) {
        ((_a = tablesOps[_b = write.table]) !== null && _a !== void 0 ? _a : (tablesOps[_b] = [])).push(write.op);
    }
    return deriveMultiRowMeta(tablesOps, schemaView);
}
export class TableScope {
    constructor(host, table) {
        this.host = host;
        this.table = table;
    }
    startAt() {
        return position(this.host.getId()); // the group's create entry
    }
    startEmpty() {
        return false; // the create entry may seed rows
    }
    baseFilter() {
        return { containsValues: { tables: [this.table] } };
    }
    wrapPayload(payload, _at) {
        const envelope = {
            action: 'row',
            table: this.table,
            op: payload,
        };
        return envelope;
    }
    unwrapPayload(payload, _at) {
        var _a, _b;
        const outer = payload;
        switch (outer['action']) {
            case 'row': {
                const envelope = outer;
                if (envelope.table !== this.table) {
                    throw new Error(`Row envelope for table '${envelope.table}' unwrapped in scope of '${this.table}'`);
                }
                return envelope.op;
            }
            case 'create': {
                const create = outer;
                const slice = {
                    action: 'rows',
                    ops: ((_b = (_a = create.initialRows) === null || _a === void 0 ? void 0 : _a[this.table]) !== null && _b !== void 0 ? _b : []),
                };
                return slice;
            }
            case 'bundle': {
                const bundle = outer;
                const ops = [];
                for (const write of bundle.writes) {
                    if (write.table === this.table)
                        ops.push(write.op);
                }
                return { action: 'rows', ops };
            }
            default:
                throw new Error(`Invalid payload action in TableScope.unwrapPayload: ${outer['action']}`);
        }
    }
    wrapMeta(innerMeta, _wrappedPayload, _at) {
        const outerMeta = { tables: json.toSet([this.table]) };
        wrapInnerMeta(outerMeta, this.table, innerMeta);
        return outerMeta;
    }
    unwrapMeta(outerMeta, _wrappedPayload, _at) {
        const innerMeta = {};
        const prefix = 't-' + this.table + '-';
        for (const key of Object.keys(outerMeta)) {
            if (key.startsWith(prefix)) {
                innerMeta[key.substring(prefix.length)] = outerMeta[key];
            }
            else if (key === 'barrier') {
                innerMeta['barrier'] = outerMeta[key];
            }
        }
        return innerMeta;
    }
    wrapFilter(filter) {
        const wrapped = {};
        if (filter.containsKeys !== undefined) {
            wrapped.containsKeys = filter.containsKeys.map((key) => key === 'barrier' ? key : 't-' + this.table + '-' + key);
        }
        if (filter.containsValues !== undefined) {
            wrapped.containsValues = {};
            for (const key of Object.keys(filter.containsValues)) {
                const wrappedKey = key === 'barrier' ? key : 't-' + this.table + '-' + key;
                wrapped.containsValues[wrappedKey] = filter.containsValues[key];
            }
        }
        return wrapped;
    }
    // Local-append check: the payload is validated through the group (when
    // selfValidate is on), and the carried meta must equal the re-derived
    // meta (meta is unhashed; a divergence here would forge visibility).
    validateWrappedPayload(wrappedPayload, wrappedMeta, at) {
        return __awaiter(this, void 0, void 0, function* () {
            const envelope = wrappedPayload;
            if (envelope.action !== 'row')
                return validationFailure("table scope can only append row envelopes");
            const schemaView = yield this.host.resolveSchemaView(at);
            const expected = deriveEnvelopeMeta(envelope, schemaView);
            if (json.toStringNormalized(wrappedMeta) !== json.toStringNormalized(expected)) {
                return validationFailure(`row envelope meta for table '${this.table}' does not match derived meta`);
            }
            if (!this.host.selfValidate())
                return validationOk();
            return wrapValidationFailure(`table scope append for '${this.table}' rejected by group validation`, yield this.host.validatePayload(wrappedPayload, at), this.host.getId());
        });
    }
}
